import {
  ClaimableJourney,
  ClaimableJourneyPropertyDetails,
  ClaimableStates,
  ClaimFormValues,
  HomeUserClaimFormValues,
} from "./types";
import {
  getPremiseTypeFromKrakenValue,
  getStreetTypeFromKrakenValue,
} from "../helper";
import { accountFormFieldMapper } from "src/api/apiHelper";
import { AccountPayer } from "../types";
import { Company } from "src/components/Projects/types";
import { formatDpNumber } from "../helpers";
import { SiteDetails } from "src/components/Premises/types";

// no longer used as we now get a single journey as a response from the api
// const findLatestJourney = (journeys: ClaimableJourney[], type: ClaimableItemType) => {
//     return journeys.reduce((p, c) => p && p[type as unknown as keyof ClaimableJourney]! > c[type as unknown as keyof ClaimableJourney]! ? p : c)
// }

// const determineJourneyOrDb = (claimableItem: ClaimableJourney) => {
//     if (claimableItem.filter(c => c[ClaimableItemType.JOURNEY]).length > 0) {
//         return ClaimableItemType.JOURNEY
//     }
//     return ClaimableItemType.DB
// }

export const determineClaimable = (
  claimableItem?: ClaimableJourney,
  userCompanyMsa?: string,
  currentUserId?: string,
  currentlySelectedCompanyMsa?: string,
  currentUserRole?: string
) => {
  if (claimableItem) {
    switch (claimableItem.claimed) {
      case true:
        //if the current user claimed the request
        if (claimableItem?.claimed_request_creator?.id === currentUserId) {
          return {
            claimableJourney: claimableItem,
            computedState: ClaimableStates.CLAIMED,
          };
        }
        // if user has access to the requesting company, then the user can see if the company and any of the other users have claimed this request (admins have access to all companies)
        if (
          claimableItem.user_access_to_requesting_company === "FULL_ACCESS" ||
          currentUserRole === "ORIGIN_ADMIN"
        ) {
          if (
            (claimableItem.claimed_request_company &&
              claimableItem.claimed_request_company.msa_number ===
                userCompanyMsa) ||
            (claimableItem.claimed_request_company &&
              claimableItem.claimed_request_company.msa_number ===
                currentlySelectedCompanyMsa)
          ) {
            return {
              claimableJourney: claimableItem,
              computedState: ClaimableStates.CLAIMED,
            };
          }
        }
        // otherwise, just show it was claimed by someone else
        return {
          claimableJourney: claimableItem,
          computedState: ClaimableStates.CLAIMED_BY_SOMEONE_ELSE,
        };
      case false:
        return {
          claimableJourney: claimableItem,
          computedState:
            claimableItem?.claimable_state === false
              ? ClaimableStates.COMPLETED
              : ClaimableStates.CLAIMABLE,
        };
      default:
        return {
          claimableJourney: claimableItem,
          computedState: ClaimableStates.NOT_FOUND,
        };
    }
  }
  return {
    claimableJourney: claimableItem,
    computedState: ClaimableStates.NOT_FOUND,
  };
};

export const getClaimDisplayAddressString = (
  address: ClaimableJourneyPropertyDetails,
  siteDetails: SiteDetails
) => {
  const {
    street_number,
    street_name,
    street_type,
    suburb,
    state,
    postcode,
    lot_number,
    dp_number,
    block_number,
    section_number,
    building_name,
    street_suffix,
  } = siteDetails;

  const { unit_number, unit_type } = address;

  const unitDetails = unit_number
    ? `${
        unit_type ? getPremiseTypeFromKrakenValue(unit_type) : "Unit"
      } ${unit_number}, `
    : "";

  const decodedBlockNumber = block_number ? `Block ${block_number}, ` : "";
  const decodedSectionNumber = section_number
    ? `Section ${section_number}, `
    : "";
  const decodedDpNumber = formatDpNumber(dp_number, state);
  const decodedBuildingName = building_name ? `${building_name}, ` : "";
  const decodedLotNumber = lot_number ? `Lot ${lot_number}, ` : "";

  const streetDetails = `${decodedBuildingName}${unitDetails}${decodedBlockNumber}${decodedSectionNumber}${decodedDpNumber}${decodedLotNumber}${street_number} ${street_name} ${getStreetTypeFromKrakenValue(
    street_type
  )} ${street_suffix ? street_suffix : ""}, ${suburb}, ${state} ${postcode}`;
  return streetDetails;
};

export const claimFormFieldMapper = (values: ClaimFormValues) => {
  return {
    msa_number: values.requestingCompany?.msaNumber,
    project: { id: values.projectId },
    site_supervisor: values.siteSupervisor,
    account: accountFormFieldMapper(values),
  };
};

export const claimValuesToRestMapper = (
  requestId: string,
  values: ClaimFormValues,
  msaNumber?: string,
  journeyId?: string,
  dbApplicationId?: string,
  projectId?: string
): FormData => {
  const formData = new FormData();

  if (journeyId) {
    formData.append("journeyId", journeyId);
  }
  if (projectId) {
    formData.append("projectId", projectId);
  }
  if (dbApplicationId) {
    formData.append("distributorApplicationId", dbApplicationId);
  }
  if (values.siteSupervisor) {
    formData.append("siteSuperVisorId", values.siteSupervisor);
  }
  if (values.electricityAccountPayer === AccountPayer.COMPANY) {
    formData.append("isOnLifeSupport", String(values.electricityLifeSupport));
  }
  if (msaNumber) {
    formData.append("requesterMsaNumber", msaNumber);
  }
  if (
    msaNumber &&
    values.electricityAccountPayer != AccountPayer.SOMEONE_ELSE
  ) {
    formData.append("msaNumber", msaNumber); // TODO: After Digital EIC released, rename to customerMsaNumber
  }

  if (values.electricityAccountPayer === AccountPayer.SOMEONE_ELSE) {
    formData.append(
      "accountDetails",
      new Blob([JSON.stringify(accountFormFieldMapper(values))], {
        type: "application/json",
      })
    );
  }

  formData.append("referenceNumber", requestId);
  if (values.claimsCustomerConsentFiles.length > 0) {
    const file = values.claimsCustomerConsentFiles[0];
    formData.append("file", file.file!);
  }
  formData.append(
    "accountPayer",
    new Blob([JSON.stringify(values.electricityAccountPayer)], {
      type: "application/json",
    })
  );

  return formData;
};

export const homeUserClaimValuesToRestMapper = (
  requestId: string,
  values: HomeUserClaimFormValues,
  journeyId?: string,
  dbApplicationId?: string
): FormData => {
  const formData = new FormData();

  if (journeyId) {
    formData.append("journeyId", journeyId);
  }
  if (dbApplicationId) {
    formData.append("distributorApplicationId", dbApplicationId);
  }
  formData.append(
    "accountDetails",
    new Blob([JSON.stringify(accountFormFieldMapper(values))], {
      type: "application/json",
    })
  );

  formData.append("referenceNumber", requestId);
  if (values.claimsCustomerConsentFiles.length > 0) {
    const file = values.claimsCustomerConsentFiles[0];
    formData.append("file", file.file!);
  }
  formData.append(
    "accountPayer",
    new Blob([JSON.stringify(values.electricityAccountPayer)], {
      type: "application/json",
    })
  );

  return formData;
};

export const claimFailedSnackbarDetails = {
  message: "Something went wrong.",
  tone: "critical",
};

export const userCompanyToRequestingCompany = (company: Company) => {
  return {
    msaNumber: company.msa_number,
    id: company.id,
    name: company.name,
    operationalTeam: company.operational_team,
  };
};
